import { produce } from 'immer'
import { create } from 'zustand'

import { getDefaultDateRange } from 'components/date-range-picker/date-defaults'
import { DEFAULT_PANEL_FIELDS } from 'features/charts-v2/constants/panel-defaults'
import {
  type BreakdownOption,
  BREAKDOWNS,
  type CustomBreakdown,
  getBreakdownById,
  METRICS,
  type MetricType,
  type SelectedFilterOption,
} from 'features/charts-v2/types'
import { type ChartPreset } from 'features/charts-v2/types/chart-types'
import { ChartConstraintsService } from 'features/charts-v2/types/ChartConstraints'
import {
  getPresetById,
  resolvePresetTimeBucket,
  resolvePresetTitle,
  SENTIMENT_COMPOSITION_PRESET,
} from 'features/charts-v2/types/ChartPresets'
import {
  type Breakdown,
  ChartPanel,
  ChartsV2State,
  ChartsV2Store,
  type GroupableBreakdown,
  type NonGroupableBreakdown,
} from 'features/charts-v2/types/filter-hash/v2'
import { foldRatingOnlyFilter } from 'features/charts-v2/utils/fold-rating-only-filter'
import { normalizeLegacyPanelSearch } from 'features/charts-v2/utils/normalize-legacy-panel-search'
import { trackChartPanelAction } from 'trackers/chart-events'

// ---------------------------------------------------------------------------
// Helper functions
// ---------------------------------------------------------------------------

/**
 * Generate a unique ID for panels
 */
export const generateId = (): string => {
  return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`
}

/** Groupable breakdown ids that require the `grouped` field on the wire. */
const GROUPABLE_IDS: ReadonlyArray<GroupableBreakdown['id']> = [
  'brand',
  'product',
  'theme',
]

/**
 * Build a correctly-typed standard Breakdown from a BreakdownOption id.
 * Groupable ids (brand / product / theme) require `grouped`; non-groupable
 * ids must NOT carry it. Using this helper instead of object literals + casts
 * lets TypeScript enforce completeness at the call site.
 */
const toStandardBreakdown = (id: BreakdownOption): Breakdown =>
  (GROUPABLE_IDS as ReadonlyArray<string>).includes(id)
    ? { kind: 'standard', id: id as GroupableBreakdown['id'], grouped: true }
    : { kind: 'standard', id: id as NonGroupableBreakdown['id'] }

// ---------------------------------------------------------------------------
// Grain alignment
// ---------------------------------------------------------------------------

/**
 * A breakdown is "sentence-grain" when its underlying model is a single
 * sentence (`POST_TEXT`), not a whole post/record. `record_count` counts
 * `POST`, so grouping it by a sentence attribute produces overlapping,
 * non-summable buckets (a post with several sentiment sentences lands in
 * several slices) and folds null-sentiment rows into a single bucket. The
 * summable count for a sentence-grain breakdown is `sentence_count`
 * (`COUNT(POST_TEXT)`).
 *
 * Grain is read from the breakdown's `model` metadata, not a hardcoded id, so
 * any future sentence-grain breakdown is covered automatically: a standard
 * breakdown whose `BREAKDOWNS[...].model === 'Sentence'` (today only
 * `sentiment`), or a custom field whose `model === 'post_text'`.
 */
export function isSentenceGrainBreakdown(
  breakdown: Breakdown | null | undefined,
): boolean {
  if (!breakdown) return false
  if (breakdown.kind === 'custom') {
    return breakdown.model === 'post_text'
  }
  const config = getBreakdownById(breakdown.id)
  return config?.model === 'Sentence'
}

/**
 * The count metric that matches a sentence-grain primary breakdown. Exposed so
 * UI seams (the metric picker) can coerce the selected metric, not just filter
 * the option list.
 */
export const SENTENCE_GRAIN_COUNT_METRIC: MetricType = METRICS.SENTENCE_COUNT.id

/**
 * Grain-align a panel's metrics to its primary breakdown.
 *
 * When the primary breakdown is sentence-grain, the post-grain count metric
 * (`record_count`) is replaced with the sentence-grain count (`sentence_count`).
 * Every other metric (rating, sentiment, and any already-present
 * `sentence_count`) is left untouched. When the primary breakdown is not
 * sentence-grain this is a pure no-op, so it is safe to run at any chart-config
 * seam, including programmatic panel builders that never pair `record_count`
 * with a sentiment breakdown.
 *
 * Only the record→sentence direction is rewritten. The inverse (coercing
 * `sentence_count` back to `record_count` when the breakdown becomes
 * post-grain) is deliberately out of scope: `sentence_count` is valid under a
 * post-grain breakdown, so there is no invalid state to prevent.
 */
export function alignMetricsToPrimaryBreakdown(
  metrics: MetricType[],
  primaryBreakdown: Breakdown | null | undefined,
): MetricType[] {
  if (!isSentenceGrainBreakdown(primaryBreakdown)) {
    return metrics
  }
  let changed = false
  const aligned = metrics.map((metric) => {
    if (metric === METRICS.RECORD_COUNT.id) {
      changed = true
      return SENTENCE_GRAIN_COUNT_METRIC
    }
    return metric
  })
  // Preserve referential identity when nothing changed so callers that key
  // effects on the array don't churn.
  return changed ? aligned : metrics
}

/**
 * Adjust a chart-type metric-options record (`{ [metricId]: enabled }`) for a
 * sentence-grain primary breakdown by disabling `record_count`. This is the
 * hard lock behind the metric picker: with a sentiment breakdown active,
 * `record_count` is not a selectable option at all. A no-op for post-grain
 * breakdowns.
 */
export function adjustMetricOptionsForBreakdown(
  options: Record<MetricType, boolean>,
  primaryBreakdown: Breakdown | null | undefined,
): Record<MetricType, boolean> {
  if (!isSentenceGrainBreakdown(primaryBreakdown)) {
    return options
  }
  return { ...options, [METRICS.RECORD_COUNT.id]: false }
}

/**
 * Set a panel's primary breakdown and grain-align its count metric in one step.
 *
 * The drag-to-create flow (`DraggableChartCardsV2`) builds a preset panel, then
 * overrides its primary breakdown with the user-selected one; dropping a pie
 * (default `record_count`) onto a sentiment breakdown must yield
 * `sentence_count`, not the invalid post-count-by-sentence combination. Owning
 * both mutations here keeps the grain lock inside the store instead of forcing
 * the consumer to re-align by hand.
 */
export const applyBreakdownToPanel = (
  panel: ChartPanel,
  breakdown: Breakdown,
): void => {
  panel.breakdowns = [breakdown, ...panel.breakdowns.slice(1)]
  panel.metrics = alignMetricsToPrimaryBreakdown(panel.metrics, breakdown)
}

/**
 * The primary breakdown to actually use for `preset` given a caller's
 * requested `candidate` (e.g. a sidebar's "selected breakdown" pill). A
 * two-breakdown preset (Theme Brand Rating: theme × brand) already fixes its
 * own secondary; overriding the primary with a candidate that collides with
 * that secondary would collapse the chart onto one dimension twice ("Brand
 * Brand Rating" instead of "Theme Brand Rating"). Falls back to the preset's
 * own default primary in that case; returns `candidate` unchanged otherwise.
 * Shared by every "pick a breakdown, then add a preset" flow —
 * `DraggableChartCardsV2` (dashboards-v2) and `build-chart-item`
 * (dashboards-v2-1) — so title/description resolution and panel
 * construction never disagree about which breakdown actually won.
 */
export const resolveEffectivePrimaryBreakdown = (
  preset: ChartPreset,
  candidate: Breakdown,
): Breakdown => {
  const candidateId =
    candidate.kind === 'standard' ? candidate.id : candidate.field
  if (
    preset.secondaryBreakdown &&
    preset.defaultPrimaryBreakdown &&
    candidateId === preset.secondaryBreakdown
  ) {
    return toStandardBreakdown(preset.defaultPrimaryBreakdown)
  }
  return candidate
}

/**
 * Build breakdown configuration from a preset
 */
const buildBreakdownsFromPreset = (preset: ChartPreset): Breakdown[] => {
  if (preset.breakdowns) return preset.breakdowns.map((b) => ({ ...b }))
  const breakdowns: Breakdown[] = []
  if (preset.defaultPrimaryBreakdown) {
    breakdowns.push(toStandardBreakdown(preset.defaultPrimaryBreakdown))
  }
  if (preset.secondaryBreakdown) {
    breakdowns.push(toStandardBreakdown(preset.secondaryBreakdown))
  }
  return breakdowns
}

/**
 * Create a panel from a chart preset
 */
export const createPanelFromPreset = (
  preset: ChartPreset,
  order: number,
): ChartPanel => {
  const { startDate, endDate } = getDefaultDateRange()

  const breakdowns = buildBreakdownsFromPreset(preset)

  return {
    ...DEFAULT_PANEL_FIELDS,
    id: generateId(),
    order,
    title: preset.label,
    isCustomTitle: false,
    chartType: preset.chartType,
    // Grain-lock the count metric to the primary breakdown so a preset whose
    // default primary is sentence-grain never ships `record_count`. No-op for
    // post-grain presets.
    metrics: alignMetricsToPrimaryBreakdown(preset.metrics, breakdowns[0]),
    breakdowns,
    dateRange: {
      startDate: Math.floor(startDate.getTime() / 1000),
      endDate: Math.floor(endDate.getTime() / 1000),
    },
    timeBucket: preset.timeBucket,
    presetId: preset.id,
    ...(preset.flowColorMode && { flowColorMode: preset.flowColorMode }),
  }
}

export const createDefaultPanel = (order: number): ChartPanel => {
  return createPanelFromPreset(SENTIMENT_COMPOSITION_PRESET, order)
}

/**
 * Apply a preset onto an EXISTING panel — as opposed to `createPanelFromPreset`,
 * which builds a brand-new one. Preserves the panel's current primary
 * breakdown, adds the preset's secondary breakdown, drops any breakdown
 * invalid for the *incoming* chart type/metrics, clamps to the new chart
 * type's max breakdown count, and realigns metrics/time bucket/title. Shared
 * by `/charts` (`ChartControlsSidebar.handlePresetSelect`) and
 * dashboards-v2-1's tile-controls sidebar so neither re-derives this ruleset.
 */
export const applyPresetToPanel = (
  panel: ChartPanel,
  preset: ChartPreset,
): Partial<ChartPanel> => {
  // Preserve current primary, fall back to preset's default
  const currentPrimary = panel.breakdowns[0]
  const primary = currentPrimary ?? preset.defaultPrimaryBreakdown

  // Build breakdowns array - need to convert to Breakdown objects
  let newBreakdowns: Breakdown[] = []
  if (preset.breakdowns) {
    // A preset that spells out its breakdowns (grouped, or more than two) is a
    // fixed shape, so it replaces the panel's own instead of extending them.
    newBreakdowns = preset.breakdowns.map((b) => ({ ...b }))
  } else if (primary) {
    if (typeof primary === 'string') {
      // Standard breakdown
      const isGroupable = ['brand', 'product', 'theme'].includes(primary)
      newBreakdowns.push({
        kind: 'standard',
        id: primary as BreakdownOption,
        ...(isGroupable && { grouped: false }),
      } as Breakdown)
    } else {
      // Custom breakdown
      newBreakdowns.push(primary as Breakdown)
    }
  }

  // Always add secondary breakdown from preset if defined (and not already present)
  if (!preset.breakdowns && preset.secondaryBreakdown) {
    const secondaryExists = newBreakdowns.some((b) => {
      if (typeof preset.secondaryBreakdown === 'string') {
        return b.kind === 'standard' && b.id === preset.secondaryBreakdown
      }
      return (
        b.kind === 'custom' &&
        b.field === (preset.secondaryBreakdown as CustomBreakdown | null)?.field
      )
    })

    if (!secondaryExists) {
      if (typeof preset.secondaryBreakdown === 'string') {
        const isGroupable = ['brand', 'product', 'theme'].includes(
          preset.secondaryBreakdown,
        )
        newBreakdowns.push({
          kind: 'standard',
          id: preset.secondaryBreakdown as BreakdownOption,
          ...(isGroupable && { grouped: false }),
        } as Breakdown)
      } else {
        newBreakdowns.push(preset.secondaryBreakdown as Breakdown)
      }
    }
  }

  // Remove breakdowns that violate the *incoming* preset's metric/chart-type
  // constraints (not the current panel's — which would incorrectly strip valid
  // secondary breakdowns before the metric flip takes effect).
  const nextConstraints = ChartConstraintsService.getConstraints(
    preset.chartType,
  )
  const nextSentimentAllowed = preset.metrics.includes(
    METRICS.SENTENCE_COUNT.id,
  )
  const nextRatingAllowed = preset.metrics[0] === METRICS.RECORD_COUNT.id
  const nextInvalidBreakdowns = [
    ...(nextConstraints.breakdowns.invalidTypes ?? []),
    ...(!nextSentimentAllowed ? [BREAKDOWNS.SENTIMENT.id] : []),
    ...(!nextRatingAllowed ? [BREAKDOWNS.RATING.id] : []),
  ]
  newBreakdowns = newBreakdowns.filter((b) => {
    const id = b.kind === 'standard' ? b.id : b.field
    return !nextInvalidBreakdowns.includes(id as BreakdownOption)
  })
  // Clamp to the new chart type's max-breakdown capacity (e.g. Heatmap -> Bar).
  newBreakdowns = newBreakdowns.slice(0, nextConstraints.breakdowns.max)

  // Apply preset configuration. The title tracks the active preset's label
  // (or dynamic template, see resolvePresetTitle) until the user customizes
  // it (see ChartPanel.isCustomTitle); v1 expressed the same intent as two
  // fields (defaultChartTitle + customTitle).
  return {
    chartType: preset.chartType,
    // Grain-lock the preset's count metric to the resolved primary breakdown
    // so applying a preset onto a preserved sentiment breakdown never yields
    // `record_count`. No-op for post-grain breakdowns.
    metrics: alignMetricsToPrimaryBreakdown(preset.metrics, newBreakdowns[0]),
    breakdowns: newBreakdowns,
    // A user-selected time bucket must survive switching between time-bucketed
    // presets (e.g. line chart -> line chart): every "over time" preset ships
    // `timeBucket: 'month'`, so unconditionally applying the preset default
    // silently reverted a chosen `day` back to `month` on each transition
    // (PROD-4517). See resolvePresetTimeBucket.
    timeBucket: resolvePresetTimeBucket(panel.timeBucket, preset),
    presetId: preset.id,
    ...(preset.flowColorMode && { flowColorMode: preset.flowColorMode }),
    ...(panel.isCustomTitle
      ? {}
      : { title: resolvePresetTitle(preset, newBreakdowns[0] ?? null) }),
  }
}

// ---------------------------------------------------------------------------
// Ghost filters (eye-toggle disable)
// ---------------------------------------------------------------------------

/** Stable empty array so a `ghostFilters[panelId] ?? NO_GHOST_FILTERS`
 *  selector never mints a fresh identity and re-renders on unrelated
 *  store updates. */
export const NO_GHOST_FILTERS: readonly SelectedFilterOption[] = []

/**
 * Keep `ghostFilters` consistent with its invariant — every entry names a
 * live panel, and only fields NOT currently in that panel's `filters` — by
 * running this after every mutation that can add, remove, or replace a
 * panel's filters, rather than have each action remember to do it itself.
 * Without this, re-adding a ghosted field through the popover (or a
 * saved-filter apply, or `syncFiltersToOtherPanels`) would leave a stale
 * ghost shadowing a live filter.
 */
function pruneGhostFilters(state: ChartsV2Store): void {
  for (const panelId of Object.keys(state.ghostFilters)) {
    const panel = state.v2.panels.find((p) => p.id === panelId)
    if (!panel) {
      delete state.ghostFilters[panelId]
      continue
    }
    const liveFields = new Set(panel.filters.map((f) => f.field))
    const ghosts = state.ghostFilters[panelId]!
    const kept = ghosts.filter((f) => !liveFields.has(f.field))
    if (kept.length === 0) {
      delete state.ghostFilters[panelId]
    } else if (kept.length !== ghosts.length) {
      state.ghostFilters[panelId] = kept
    }
  }
}

/** Same rule as `pruneGhostFilters`, for `hydrate`'s plain (non-draft) `set`
 *  call — it builds a fresh state object rather than mutating one. */
function pruneGhostFiltersForHydration(
  ghostFilters: Record<string, SelectedFilterOption[]>,
  panels: ChartPanel[],
): Record<string, SelectedFilterOption[]> {
  const next: Record<string, SelectedFilterOption[]> = {}
  for (const [panelId, ghosts] of Object.entries(ghostFilters)) {
    const panel = panels.find((p) => p.id === panelId)
    if (!panel) continue
    const liveFields = new Set(panel.filters.map((f) => f.field))
    const kept = ghosts.filter((f) => !liveFields.has(f.field))
    if (kept.length > 0) next[panelId] = kept
  }
  return next
}

/** The most panels one charts page (one hash) holds. */
export const MAX_PANELS = 3

// ---------------------------------------------------------------------------
// Initial state
// ---------------------------------------------------------------------------

const initialState: ChartsV2State = {
  v2: {
    panels: [],
    globalSettings: {
      syncYAxis: true,
      syncXAxis: true,
      syncDisplaySettings: false,
      syncChartType: true,
      syncMetrics: true,
      syncBreakdowns: true,
    },
    activePanelId: null,
  },
}

// ---------------------------------------------------------------------------
// Store
// ---------------------------------------------------------------------------

export const useChartsV2Store = create<ChartsV2Store>()((set, get) => {
  // Shared by every action that can add, remove, or replace a panel's
  // filters: runs the recipe, then re-establishes the ghost invariant in one
  // place. See `pruneGhostFilters`.
  const setPanels = (recipe: (state: ChartsV2Store) => void): void => {
    set(
      produce((state: ChartsV2Store) => {
        recipe(state)
        pruneGhostFilters(state)
      }),
    )
  }

  return {
    ...initialState,
    ghostFilters: {},

    hydrate: (state: ChartsV2State) => {
      const migratedPanels = state.v2.panels.map((panel) => {
        const withDefaults: ChartPanel = { ...DEFAULT_PANEL_FIELDS, ...panel }
        // Backfill datapointLabelsMode for panels persisted before it existed,
        // preserving only a deliberate `false` (labels turned off) as 'off'.
        // Panels persisted while the mode was tri-state normalize 'auto' (the
        // pre-culling default) to 'on' — both mean occlusion-culled labels now.
        const persistedMode = (panel as Partial<ChartPanel>).datapointLabelsMode
        if (persistedMode == null) {
          withDefaults.datapointLabelsMode =
            (panel as Partial<ChartPanel>).showDatapointLabels === false
              ? 'off'
              : 'on'
        } else if (persistedMode === 'auto') {
          withDefaults.datapointLabelsMode = 'on'
        }
        // Backfill isCustomTitle for panels persisted before the field existed.
        // A title that still equals its preset's label is template-derived;
        // anything else is a user edit we must preserve across preset switches.
        if (typeof (panel as Partial<ChartPanel>).isCustomTitle !== 'boolean') {
          const preset = withDefaults.presetId
            ? getPresetById(withDefaults.presetId)
            : undefined
          // A title that matches either the static preset label OR the
          // dynamically-resolved template title (e.g. "Brand Brand Rating") is
          // considered template-derived, not a user edit.
          withDefaults.isCustomTitle = preset
            ? withDefaults.title !== preset.label &&
              withDefaults.title !==
                resolvePresetTitle(preset, withDefaults.breakdowns[0] ?? null)
            : true
        }
        // Heal persisted panels that paired `record_count` with a sentence-grain
        // primary breakdown (a combo the UI no longer lets you build). No-op for
        // post-grain breakdowns and for panels already using `sentence_count`.
        withDefaults.metrics = alignMetricsToPrimaryBreakdown(
          withDefaults.metrics,
          withDefaults.breakdowns[0] ?? null,
        )
        // Ask Yogi stores "reviews only" as an `is_rating_only` filter the
        // rating-type control can't see; fold it into the score filter it reads.
        withDefaults.filters = foldRatingOnlyFilter(withDefaults.filters)
        // Repair legacy / corrupted search shapes (v1 operator-array, or the
        // operator-as-keyword Record left by the first migration attempt). Shared
        // with the request boundary in `useChartQuery` so every code path heals
        // identically.
        return normalizeLegacyPanelSearch(withDefaults)
      })

      set({
        v2: {
          panels: migratedPanels,
          globalSettings: state.v2.globalSettings,
          activePanelId: state.v2.activePanelId,
        },
        // In-session navigation (a hash pop) restores the same generated panel
        // ids, so ghosts for those panels survive; a saved view arrives with
        // fresh ids and drops everything. Never a blanket clear.
        ghostFilters: pruneGhostFiltersForHydration(
          get().ghostFilters,
          migratedPanels,
        ),
      })
    },

    addPanel: () => {
      let newPanelId = ''

      setPanels((state) => {
        if (state.v2.panels.length >= MAX_PANELS) return

        const activePanel = state.v2.panels.find(
          (p) => p.id === state.v2.activePanelId,
        )

        let newPanel: ChartPanel

        if (activePanel) {
          // Duplicate active panel
          newPanel = {
            ...JSON.parse(JSON.stringify(activePanel)), // Deep clone
            id: generateId(),
            order: state.v2.panels.length,
          }
        } else {
          // Create default panel
          newPanel = createDefaultPanel(state.v2.panels.length)
        }

        state.v2.panels.push(newPanel)
        newPanelId = newPanel.id

        if (state.v2.panels.length > 1) {
          for (const panel of state.v2.panels) {
            panel.showLegend = false
          }
        }

        trackChartPanelAction({
          action: 'added',
          panel_count: state.v2.panels.length,
        })
      })

      return newPanelId
    },

    removePanel: (panelId: string): string | null => {
      let newActivePanelId: string | null = null
      setPanels((state) => {
        if (state.v2.panels.length <= 1) return

        state.v2.panels = state.v2.panels.filter((p) => p.id !== panelId)

        if (state.v2.activePanelId === panelId) {
          newActivePanelId = state.v2.panels[0]?.id ?? null
          state.v2.activePanelId = newActivePanelId
        } else {
          newActivePanelId = state.v2.activePanelId
        }

        // Reorder remaining panels
        state.v2.panels.forEach((panel, index) => {
          panel.order = index
        })

        trackChartPanelAction({
          action: 'removed',
          panel_count: state.v2.panels.length,
        })
      })
      return newActivePanelId
    },

    duplicatePanel: (panelId: string) => {
      // Deliberately does not copy `ghostFilters[panelId]`: the duplicate
      // copies the panel's real filters, and a copy that arrives pre-disabled
      // has no persisted state to explain why. If this is ever revisited, the
      // ghost array must be deep-cloned too, or the two panels would alias the
      // same option objects.
      setPanels((state) => {
        if (state.v2.panels.length >= MAX_PANELS) return

        const sourcePanel = state.v2.panels.find((p) => p.id === panelId)
        if (!sourcePanel) return

        const duplicatedPanel: ChartPanel = {
          ...JSON.parse(JSON.stringify(sourcePanel)), // Deep clone
          id: generateId(),
          order: state.v2.panels.length,
        }

        state.v2.panels.push(duplicatedPanel)

        trackChartPanelAction({
          action: 'duplicated',
          panel_count: state.v2.panels.length,
        })
      })
    },

    updatePanel: (panelId: string, updates: Partial<ChartPanel>) => {
      setPanels((state) => {
        const panelIndex = state.v2.panels.findIndex((p) => p.id === panelId)
        if (panelIndex !== -1) {
          state.v2.panels[panelIndex] = {
            ...state.v2.panels[panelIndex]!,
            ...updates,
          }
        }
      })
    },

    syncFiltersToOtherPanels: (sourcePanelId: string) => {
      // Deliberately does not sync `ghostFilters`: `source.filters` already
      // excludes any field the source has disabled, so a target that lacks
      // that field afterward is correct on its own terms — "sync filters"
      // means "match the source's live filters," not its UI toggles.
      setPanels((state) => {
        const source = state.v2.panels.find((p) => p.id === sourcePanelId)
        if (!source) return
        for (const panel of state.v2.panels) {
          if (panel.id === sourcePanelId) continue
          // Deep-clone per target so panels never share array references.
          // Same idiom as duplicatePanel.
          const cloned = JSON.parse(
            JSON.stringify({
              filters: source.filters,
              searchTerms: source.searchTerms,
              searchCriteria: source.searchCriteria,
              smartSearchEnabled: source.smartSearchEnabled,
            }),
          )
          panel.filters = cloned.filters
          panel.searchTerms = cloned.searchTerms
          panel.searchCriteria = cloned.searchCriteria
          panel.smartSearchEnabled = cloned.smartSearchEnabled
        }
      })
    },

    syncPanelFieldsToOtherPanels: (sourcePanelId, fields) => {
      setPanels((state) => {
        const source = state.v2.panels.find((p) => p.id === sourcePanelId)
        if (!source) return
        for (const panel of state.v2.panels) {
          if (panel.id === sourcePanelId) continue
          for (const field of fields) {
            // Deep-clone per target so panels never share array/object
            // references. Same idiom as syncFiltersToOtherPanels.
            ;(panel as Record<string, unknown>)[field] = JSON.parse(
              JSON.stringify(source[field]),
            )
          }
        }
      })
    },

    updateFilterSelection: (panelId, field, operator, values, rangeData) => {
      setPanels((state) => {
        const panel = state.v2.panels.find((p) => p.id === panelId)
        if (!panel) return

        const existingFilters = panel.filters || []
        const existingIndex = existingFilters.findIndex(
          (f) => f.field === field,
        )

        // Detect delete operation: empty values AND no rangeData of any kind.
        // Window filters (score/post_score) send empty values + {min,max,valueType}
        // and numeric filters send empty values + {numericValue}; either should
        // be treated as an update, not a delete.
        const isDeleteOperation =
          values.length === 0 &&
          rangeData?.numericValue === undefined &&
          rangeData?.min === undefined &&
          rangeData?.max === undefined &&
          rangeData?.valueType === undefined

        if (isDeleteOperation && existingIndex >= 0) {
          // Remove the filter entirely
          panel.filters = existingFilters.filter((f) => f.field !== field)
          return
        }

        const newFilter = {
          field,
          operator,
          values,
          ...(rangeData || {}),
        }

        let newFilters
        if (existingIndex >= 0) {
          // Update existing filter
          newFilters = [...existingFilters]
          newFilters[existingIndex] = newFilter
        } else {
          // Add new filter
          newFilters = [...existingFilters, newFilter]
        }

        panel.filters = newFilters
      })
    },

    disableFilter: (panelId, field) => {
      // Stash and remove inside the SAME recipe: two separate `set` calls would
      // render an intermediate frame where the filter is simply gone (no ghost
      // yet), so `filtersToShow` would drop the card and it would flicker/lose
      // its local hover state before reappearing struck through.
      setPanels((state) => {
        const panel = state.v2.panels.find((p) => p.id === panelId)
        if (!panel) return
        const index = panel.filters.findIndex((f) => f.field === field)
        if (index === -1) return
        const [ghosted] = panel.filters.splice(index, 1)
        if (!ghosted) return
        const existing = state.ghostFilters[panelId] ?? []
        state.ghostFilters[panelId] = [
          ...existing.filter((f) => f.field !== field),
          ghosted,
        ]
      })
    },

    enableFilter: (panelId, field) => {
      setPanels((state) => {
        const ghosts = state.ghostFilters[panelId]
        if (!ghosts) return
        const index = ghosts.findIndex((f) => f.field === field)
        if (index === -1) return
        // Splice the stored option out verbatim (operator, values, min, max,
        // valueType intact) rather than going through `updateFilterSelection`,
        // which rebuilds a fresh `{ field, operator, values, ...rangeData }`
        // literal and would need every rangeData key reconstructed by hand.
        const [restored] = ghosts.splice(index, 1)
        if (!restored) return
        if (ghosts.length === 0) delete state.ghostFilters[panelId]
        const panel = state.v2.panels.find((p) => p.id === panelId)
        if (!panel) return
        panel.filters = [
          ...panel.filters.filter((f) => f.field !== field),
          restored,
        ]
      })
    },

    dropGhost: (panelId, field) => {
      setPanels((state) => {
        const ghosts = state.ghostFilters[panelId]
        if (!ghosts) return
        const kept = ghosts.filter((f) => f.field !== field)
        if (kept.length === 0) delete state.ghostFilters[panelId]
        else state.ghostFilters[panelId] = kept
      })
    },

    updateGrouping: (panelId, breakdownIndex, enabled) => {
      set(
        produce((state: ChartsV2Store) => {
          const panel = state.v2.panels.find((p) => p.id === panelId)
          if (!panel) return

          const target = panel.breakdowns[breakdownIndex]
          if (target?.kind === 'standard' && 'grouped' in target) {
            panel.breakdowns[breakdownIndex] = { ...target, grouped: enabled }
          }
        }),
      )
    },

    setGlobalSettings: (settings) => {
      set(
        produce((state: ChartsV2Store) => {
          state.v2.globalSettings = {
            ...state.v2.globalSettings,
            ...settings,
          }
        }),
      )
    },

    setActivePanelId: (id) => {
      set(
        produce((state: ChartsV2Store) => {
          state.v2.activePanelId = id
        }),
      )
    },

    toApiPayload: () => {
      const state = get()
      return {
        v2: state.v2,
      }
    },

    resetChartsV2: () => {
      // `set` shallow-merges; `ghostFilters` isn't part of `initialState` (it's
      // on `ChartsV2Store`, not `ChartsV2State` — see the field's doc comment),
      // so it must be reset explicitly here or a stale ghost would survive a
      // project switch.
      set({ ...initialState, ghostFilters: {} })
    },
  }
})
